import React, { useState } from 'react';
import './ConnectOverlay.css';
import { useHedera } from '../../contexts/HederaContext';
import PrivacyPolicy from '../Legal/PrivacyPolicy';
import TermsOfService from '../Legal/TermsOfService';
import FAQ from '../Help/FAQ';
import VersionDisplay from './VersionDisplay';

function ConnectOverlay({ setShowStartDashboard }) {
  const { accountId, connect } = useHedera();
  const [isLoading, setIsLoading] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Featured galleries from real users
  const featuredGalleries = [
    {
      id: 'gallery-1',
      title: 'Empty',
      creator: 'watusi',
      views: '12.5K',
      thumbnail: `${process.env.PUBLIC_URL}/images/thumb-1.png`,
      isPopular: true,
      url: 'https://immuta.art/hash-gallery/?gallery=empty'
    },
    {
      id: 'gallery-2',
      title: 'Cloud\'s Gallery',
      creator: 'cloudovergarden',
      views: '8.2K',
      thumbnail: `${process.env.PUBLIC_URL}/images/thumb-2.png`,
      isPopular: true,
      url: 'https://immuta.art/hash-gallery/?gallery=test-2-1'
    },
    {
      id: 'gallery-3',
      title: 'XTest',
      creator: 'watusi',
      views: '15.7K',
      thumbnail: `${process.env.PUBLIC_URL}/images/thumb-3.png`,
      isPopular: true,
      url: 'https://immuta.art/hash-gallery/?gallery=xtest'
    }
  ];

  const handleButtonClick = async () => {
    if (isLoading) return; // Prevent multiple clicks
    
    setIsLoading(true);
    
    try {
      if (accountId) {
        // If wallet is connected, go to gallery
        if (setShowStartDashboard) {
          setShowStartDashboard(true);
        }
      } else {
        // If wallet is not connected, connect it
        await connect();
      }
    } catch (err) {
      console.error('Connection failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="connect-overlay">
      {/* Header Navigation */}
      <div className="site-header">
        <div className="header-content">
          <div className="logo-section">
            <img 
              src={`${process.env.PUBLIC_URL}/immuta_logo.svg`}
              alt="Immuta Hash Gallery" 
              className="site-logo"
            />
            <div className="site-title-container">
              <h1 className="site-title">Immuta Hash Gallery</h1>
              <div className="beta-badge">BETA</div>
            </div>
          </div>
          <div className="header-nav">
            {accountId && (
              <div className="header-token-icon">
                <img 
                  src={`${process.env.PUBLIC_URL}/static/media/market_token_icon.png`} 
                  alt="Access Token" 
                  className="token-icon" 
                  title="Access Token" 
                />
              </div>
            )}
            <button 
              onClick={handleButtonClick}
              className="header-cta-button"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : (accountId ? 'My Galleries' : 'Start Your Gallery')}
            </button>
          </div>
        </div>
      </div>

      {/* Launch Announcement Banner */}
      <div className="launch-announcement-banner">
        <div className="announcement-content">
          <div className="announcement-text">
            <h4>🎉 We just launched!</h4>
            <p>Get the limited edition <strong>Immuta Market Key: Founders Edition</strong></p>
          </div>
          <div className="announcement-visual">
            <img 
              src={`${process.env.PUBLIC_URL}/images/nft-preview_1.gif`} 
              alt="Immuta Market Key NFT" 
              className="nft-preview-gif"
            />
          </div>
          <div className="announcement-cta">
            <a 
              href="https://sentx.io/nft-manager/hash-gallery-founder-s-edition-market-access" 
              target="_blank" 
              rel="noopener noreferrer"
              className="buy-nft-button"
            >
              Get Founders Edition
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="connect-overlay__content">
        {/* Hero Gallery Section */}
        <div className="hero-gallery-section">
          <a 
            href={featuredGalleries[0].url} 
            className="featured-gallery"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="gallery-hero-image">
              <img 
                src={featuredGalleries[0].thumbnail} 
                alt={featuredGalleries[0].title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
              />
            </div>
            <div className="gallery-hero-info">
              <h2>{featuredGalleries[0].title}</h2>
              <div className="gallery-meta">
                <span className="creator">@{featuredGalleries[0].creator}</span>
                <span className="views">{featuredGalleries[0].views} Visits</span>
              </div>
            </div>
          </a>
          
          <div className="cta-section">
            <h3>CREATE YOUR IMMERSIVE GALLERY, SHARE YOUR COLLECTION</h3>
            <button 
              onClick={handleButtonClick}
              className="main-cta-button"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : (accountId ? 'Enter Gallery' : 'Start Your Gallery')}
            </button>
          </div>
        </div>

        {/* User Galleries */}
        <div className="trending-section">
          <h2>User Galleries</h2>
        </div>

        {/* Gallery List */}
        <div className="gallery-list">
          {featuredGalleries.slice(1).map(gallery => (
            <a 
              key={gallery.id} 
              href={gallery.url}
              className="gallery-item"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <img 
                src={gallery.thumbnail} 
                alt={gallery.title}
                className="gallery-wide-image"
              />
              <div className="gallery-overlay">
                <div className="gallery-item-info">
                  <h3>{gallery.title}</h3>
                  <div className="gallery-item-meta">
                    <span className="creator">@{gallery.creator}</span>
                    <span className="views">{gallery.views} Visits</span>
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* How It Works Section */}
        <div className="how-it-works-section">
          <div className="how-it-works-header">
            <h2>How It Works</h2>
            <p className="how-it-works-subtitle">
              Transform your digital art collection into an immersive 3D gallery experience
            </p>
          </div>

          <div className="how-it-works-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Connect Your Wallet</h3>
                <p>
                  Connect your digital wallet to access your art collection. 
                  We use <strong>Hedera</strong> - a secure, eco-friendly network 
                  that's fast and affordable for creators.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Choose Your Gallery Style</h3>
                <p>
                  Select from beautiful 3D gallery templates. Whether you prefer 
                  a classic museum feel or modern hexagonal spaces, we have the 
                  perfect setting for your art.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Curate Your Collection</h3>
                <p>
                  Your digital art automatically appears in your library. 
                  Drag and drop to arrange pieces exactly how you want them 
                  displayed in your virtual space.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Share Your Gallery</h3>
                <p>
                  Generate a shareable link to your gallery. Friends, collectors, 
                  and art enthusiasts can walk through your curated space and 
                  experience your collection like never before.
                </p>
              </div>
            </div>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <h4>For Art Lovers</h4>
              <p>
                Showcase your digital art collection in beautiful, immersive 3D spaces. 
                Perfect for collectors, artists, and anyone who loves visual art.
              </p>
            </div>

            <div className="feature-card">
              <h4>Share & Discover</h4>
              <p>
                Create shareable galleries that anyone can visit. Discover amazing 
                collections from creators around the world.
              </p>
            </div>

            <div className="feature-card">
              <h4>Lightning Fast</h4>
              <p>
                Built on Hedera's fast, secure network. Your galleries load instantly 
                and transactions cost just pennies, not dollars.
              </p>
            </div>
          </div>

          <div className="why-hedera-section">
            <h3>Why Hedera?</h3>
            <p>
              We chose Hedera because it's <strong>environmentally friendly</strong>, 
              <strong>incredibly fast</strong>, and <strong>affordable</strong>. Unlike other 
              blockchain networks, Hedera uses minimal energy while providing enterprise-grade 
              security. This means you can focus on your art, not on high fees or slow loading times.
            </p>
            <div className="hedera-benefits">
              <div className="benefit">
                <span className="benefit-icon">🌱</span>
                <span>Carbon Negative</span>
              </div>
              <div className="benefit">
                <span className="benefit-icon">💸</span>
                <span>Low Fees</span>
              </div>
              <div className="benefit">
                <span className="benefit-icon">⚡</span>
                <span>Instant Transactions</span>
              </div>
            </div>
          </div>

          <div className="getting-started-cta">
            <h3>Ready to Create Your Gallery?</h3>
            <p>Join thousands of creators sharing their art in immersive 3D spaces</p>
            <button 
              onClick={handleButtonClick}
              className="secondary-cta-button"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : (accountId ? 'View My Galleries' : 'Get Started Now')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Simplified Footer */}
      <div className="connect-overlay__footer">
        <div className="footer-content">
          <div className="footer-left">
            <span>© 2025 a project by <a href="https://vivacarta.net">Viva Carta</a></span>
            <div className="footer-links">
              <button onClick={() => setShowPrivacy(true)} className="footer-link">Privacy</button>
              <span>•</span>
              <button onClick={() => setShowTerms(true)} className="footer-link">Terms</button>
              <span>•</span>
              <button onClick={() => setShowHelp(true)} className="footer-link">Help</button>
              <span>•</span>
              <VersionDisplay style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '11px' }} />
            </div>
          </div>
          
          <div className="footer-center">
            Create immersive 3D galleries to showcase your digital art collection
          </div>
          
          <div className="footer-right">
            I—A
          </div>
        </div>
      </div>
      
      {/* Legal Modals */}
      {showPrivacy && (
        <PrivacyPolicy onClose={() => setShowPrivacy(false)} />
      )}
      
      {showTerms && (
        <TermsOfService onClose={() => setShowTerms(false)} />
      )}
      
      {showHelp && (
        <FAQ onClose={() => setShowHelp(false)} />
      )}
    </div>
  );
}

export default ConnectOverlay; 